import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, VStack, useBreakpointValue } from '@chakra-ui/react';
import { isComposerSubmitShortcut } from './composerSubmitShortcut.ts';
import {
	COMPOSER_ENTRY_BODY_FONT_SIZE,
	COMPOSER_ENTRY_BODY_FONT_WEIGHT,
	COMPOSER_ENTRY_BODY_LINE_HEIGHT,
	COMPOSER_ENTRY_BODY_MIN_H,
	COMPOSER_ENTRY_BODY_PADDING_BOTTOM,
	COMPOSER_ENTRY_BODY_PADDING_TOP,
	COMPOSER_ENTRY_ROW_GAP,
	COMPOSER_ENTRY_TITLE_FONT_SIZE,
	COMPOSER_TEXTAREA_MAX_H,
	COMPOSER_ENTRY_TITLE_FONT_WEIGHT,
	COMPOSER_ENTRY_TITLE_LINE_HEIGHT,
	COMPOSER_ENTRY_TITLE_PADDING_TOP,
	getComposerNotePlaceholder,
} from './composerConstants.ts';
import {
	clearEmptyEditable,
	insertPlainTextFromClipboard,
	normalizeSingleLineEditableText,
	setEditableText,
} from './contentEditableUtils.ts';
import { joinNoteComposerValue, splitNoteComposerValue } from './noteComposerValue.ts';

interface ComposerNoteEditorProps {
	value: string;
	onChange: (value: string) => void;
	isPrivate: boolean;
	disabled: boolean;
	/** Takes focus when the composer opens or the user switches to the Records tab. */
	shouldFocus: boolean;
}

/**
 * Note entry for the Records tab. The composer presents title/body rows like the task composer,
 * while preserving the existing note storage contract: one plain-text description where the first
 * newline-delimited line is the note title.
 */
const ComposerNoteEditor: React.FC<ComposerNoteEditorProps> = (props) => {
	const { value, onChange, isPrivate, disabled, shouldFocus } = props;
	const titleRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

	const { title, body } = useMemo(() => splitNoteComposerValue(value), [value]);
	const placeholder = getComposerNotePlaceholder(isMobile, isPrivate);
	const bodyPlaceholder =
		title.trim() && !body.trim() ? (placeholder.bodyAfterTitle ?? placeholder.body) : placeholder.body;

	const syncTitle = useCallback(() => {
		const element = titleRef.current;
		if (!element) return;
		const nextTitle = normalizeSingleLineEditableText(element.innerText);
		if (element.innerText !== nextTitle) element.innerText = nextTitle;
		onChange(joinNoteComposerValue(nextTitle, body));
		clearEmptyEditable(element);
	}, [body, onChange]);

	const syncBody = useCallback(() => {
		const element = bodyRef.current;
		if (!element) return;
		const nextBody = element.innerText === '\n' ? '' : element.innerText;
		onChange(joinNoteComposerValue(title, nextBody));
		clearEmptyEditable(element);
	}, [onChange, title]);

	// Enter alone moves to the field below; the submit chord belongs to the composer around this,
	// so it has to pass through rather than be spent on the hop.
	const handleTitleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' || isComposerSubmitShortcut(event)) return;
		event.preventDefault();
		bodyRef.current?.focus();
	}, []);

	const handleTitlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLDivElement>) => {
			event.preventDefault();
			const text = event.clipboardData.getData('text/plain').replace(/\r\n/g, '\n');
			const [nextTitle = '', ...bodyLines] = text.split('\n');
			document.execCommand('insertText', false, nextTitle);
			const pastedTitle = normalizeSingleLineEditableText(titleRef.current?.innerText ?? '');
			if (bodyLines.length > 0) {
				const pastedBody = bodyLines.join('\n');
				const nextBody = [body, pastedBody].filter(Boolean).join(body ? '\n' : '');
				onChange(joinNoteComposerValue(pastedTitle, nextBody));
				requestAnimationFrame(() => setEditableText(bodyRef.current, nextBody));
			} else {
				onChange(joinNoteComposerValue(pastedTitle, body));
			}
		},
		[body, onChange],
	);

	useEffect(() => {
		setEditableText(titleRef.current, title);
	}, [title]);

	useEffect(() => {
		setEditableText(bodyRef.current, body);
	}, [body]);

	useEffect(() => {
		if (shouldFocus) titleRef.current?.focus();
	}, [shouldFocus]);

	return (
		<VStack align="stretch" gap={COMPOSER_ENTRY_ROW_GAP} px={4} pt={2} pb={4} minH="48px">
			<Box
				ref={titleRef}
				contentEditable={!disabled}
				suppressContentEditableWarning
				role="textbox"
				aria-multiline="false"
				aria-label="Note title"
				data-placeholder={placeholder.title}
				onInput={syncTitle}
				onPaste={handleTitlePaste}
				onKeyDown={handleTitleKeyDown}
				fontSize={COMPOSER_ENTRY_TITLE_FONT_SIZE}
				fontWeight={COMPOSER_ENTRY_TITLE_FONT_WEIGHT}
				color="fg"
				fontFamily="body"
				lineHeight={COMPOSER_ENTRY_TITLE_LINE_HEIGHT}
				pt={COMPOSER_ENTRY_TITLE_PADDING_TOP}
				outline="none"
				wordBreak="break-word"
				whiteSpace="pre-wrap"
				opacity={disabled ? 0.6 : 1}
				cursor={disabled ? 'default' : 'text'}
				css={{
					'&:empty::before': {
						content: 'attr(data-placeholder)',
						color: 'fg.subtle',
						pointerEvents: 'none',
					},
				}}
			/>
			<Box
				ref={bodyRef}
				contentEditable={!disabled}
				suppressContentEditableWarning
				role="textbox"
				aria-multiline="true"
				aria-label="Note details"
				data-placeholder={bodyPlaceholder}
				onInput={syncBody}
				onPaste={insertPlainTextFromClipboard}
				// Capped inline on desktop so a long note does not take the bar to the viewport's height;
				// uncapped in the mobile sheet, whose body is the one scroller.
				maxH={{ base: 'none', md: `${COMPOSER_TEXTAREA_MAX_H}px` }}
				overflowY={{ base: 'visible', md: 'auto' }}
				fontSize={COMPOSER_ENTRY_BODY_FONT_SIZE}
				fontWeight={COMPOSER_ENTRY_BODY_FONT_WEIGHT}
				color="fg"
				fontFamily="body"
				lineHeight={COMPOSER_ENTRY_BODY_LINE_HEIGHT}
				minH={{ md: COMPOSER_ENTRY_BODY_MIN_H }}
				pt={COMPOSER_ENTRY_BODY_PADDING_TOP}
				pb={COMPOSER_ENTRY_BODY_PADDING_BOTTOM}
				outline="none"
				wordBreak="break-word"
				whiteSpace="pre-wrap"
				opacity={disabled ? 0.6 : 1}
				cursor={disabled ? 'default' : 'text'}
				css={{
					'&:empty::before': {
						content: 'attr(data-placeholder)',
						color: 'fg.subtle',
						pointerEvents: 'none',
					},
				}}
			/>
		</VStack>
	);
};

export default ComposerNoteEditor;
